import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Contact, Id, getObjectId } from '@hero/schema';
import { useContactListApi, useContactApi } from '@hero/api-hooks';
import { useFeature } from '@hero/web-context';
import { SelectAsync, type SelectionValue } from '../../../app/Select';
import { ContactEditModal } from '../Modal/ContactEditModal';

/** Contact selection value: Contact | Contact[] | Id | Id[] | undefined */
export type ContactSelectionValue = SelectionValue<Contact>;

export interface ContactSelectorProps {
  selectedContacts?: ContactSelectionValue;
  /** Called when selection changes. Receives contacts or undefined when none selected. */
  onContactsChange: (contacts: Contact[] | undefined) => void;
  /** when contact is edited/created from the modal. */
  onContactSave?: (contact: Contact) => void;
  /** the thid party customer id to filter contacts by. */
  customerId?: Id;
  allowCreateNew?: boolean;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  placeholder?: string;
  popoverZIndex?: string;
  disablePortal?: boolean;
  mode?: 'single' | 'multi';
  /** Trigger classes — lets a host match its own control sizing. */
  className?: string;
}

/**
 * ContactSelector - Select or create contact (default: multi selection).
 * Uses listQuery (raw Contact[]) and singleQuery (useContactApi(undefined)) for resolution.
 */
export const ContactSelector = React.memo(function ContactSelector({
  selectedContacts,
  onContactsChange,
  customerId,
  onContactSave,
  allowCreateNew: allowCreateNewProp,
  disabled = false,
  placeholder,
  label,
  required,
  popoverZIndex,
  disablePortal,
  mode = 'multi',
  className,
}: ContactSelectorProps) {
  const { t } = useTranslation(['m-contact', 'c-main']);
  const { isEnabled: canCreateContact } = useFeature('contact_module', 'sub_create_contact');
  const { isEnabled: canEditContact } = useFeature('contact_module', 'sub_edit_contact');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingContactId, setEditingContactId] = useState<Id | undefined>(undefined);

  const listQueryParams = useMemo(
    () => ({
      customerId: customerId != null ? getObjectId(customerId) : undefined,
    }),
    [customerId],
  );

  const contactSingleQuery = useContactApi(undefined);
  const contactListQuery = useContactListApi(listQueryParams);

  const allowCreateNew = allowCreateNewProp !== false && canCreateContact;

  const openCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
    setEditingContactId(undefined);
  }, []);

  const closeModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setEditingContactId(undefined);
  }, []);

  const handleEdit = useCallback((contact: Contact) => {
    setEditingContactId(contact._id);
  }, []);

  const handleContactSave = useCallback(
    (contact: Contact) => {
      onContactSave?.(contact);
      closeModal();
    },
    [onContactSave, closeModal],
  );

  const isModalOpen =
    (isCreateModalOpen && canCreateContact) || (editingContactId != null && canEditContact);

  return (
    <div className="space-y-3">
      <SelectAsync<Contact>
        value={selectedContacts}
        listQuery={contactListQuery}
        singleQuery={contactSingleQuery}
        onChangeSelect={onContactsChange}
        getItemId={c => c._id}
        filterSelectionToCurrentList={true}
        placeholder={placeholder || t('modal.select_contact_placeholder')}
        searchPlaceholder={t('modal.search_contact_placeholder')}
        emptyMessage={t('modal.no_contacts_message')}
        loadingMessage={t('c-main:async_operations.loading')}
        createNewLabel={t('c-main:crud_buttons.create_new')}
        onCreateNew={openCreateModal}
        allowCreateNew={allowCreateNew}
        allowEdit={canEditContact}
        onEdit={handleEdit}
        keepOpenOnEdit={editingContactId != null}
        mode={mode}
        disabled={disabled}
        label={label}
        required={required}
        className={className}
        popoverZIndex={popoverZIndex}
        disablePortal={disablePortal}
        getOptionDisplay={contact => ({
          label: contact.name,
          desc: contact.phone,
          subDesc: contact.email,
        })}
        optionDisplayStyle={{ color: 'yellow' }}
      />
      <ContactEditModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onContactSaved={handleContactSave}
        contactId={editingContactId}
        customerId={customerId}
        allowOutsideClickClose={false}
      />
    </div>
  );
});
